/*
  PeekWild US: minimal progressive enhancement.
  - Reveal: elements with .pw-reveal / .pw-reveal-media fade in as they enter the viewport.
  - Sticky buy bar: [data-pw-sticky-bar] shows once its target ([data-pw-sticky-target]) scrolls out of view.
  Without this script everything is simply visible.
*/
(() => {
  const root = document.documentElement;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const initReveal = (scope = document) => {
    const items = scope.querySelectorAll('.pw-reveal:not(.is-in), .pw-reveal-media:not(.is-in)');
    if (!items.length) return;
    if (reduceMotion || !('IntersectionObserver' in window)) {
      items.forEach((el) => el.classList.add('is-in'));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          io.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );
    items.forEach((el) => io.observe(el));
  };

  const initStickyBars = (scope = document) => {
    scope.querySelectorAll('[data-pw-sticky-bar]').forEach((bar) => {
      const target = document.querySelector(bar.dataset.pwStickyBar || '[data-pw-sticky-target]');
      if (!target || !('IntersectionObserver' in window)) return;
      new IntersectionObserver(([entry]) => {
        const hidden = entry.isIntersecting || entry.boundingClientRect.top > 0;
        bar.toggleAttribute('data-visible', !hidden);
        bar.setAttribute('aria-hidden', hidden ? 'true' : 'false');
        bar.inert = hidden;
      }).observe(target);
    });
  };

  // Product filter buttons: [data-pw-filter-group="<list id>"] > [data-pw-filter="<handle|all>"]
  // show items whose data-pw-cats contains the handle.
  const initFilters = (scope = document) => {
    scope.querySelectorAll('[data-pw-filter-group]').forEach((group) => {
      const list = document.getElementById(group.dataset.pwFilterGroup);
      if (!list || group.dataset.pwReady) return;
      group.dataset.pwReady = 'true';
      group.addEventListener('click', (event) => {
        const button = event.target.closest('[data-pw-filter]');
        if (!button) return;
        const value = button.dataset.pwFilter;
        group.querySelectorAll('[data-pw-filter]').forEach((b) => b.setAttribute('aria-pressed', String(b === button)));
        list.querySelectorAll('[data-pw-cats]').forEach((item) => {
          item.hidden = value !== 'all' && !item.dataset.pwCats.split(' ').includes(value);
        });
        list.parentElement.scrollTo({ left: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
        list.closest('[data-pw-carousel]')?.dispatchEvent(new CustomEvent('pw:filtered'));
      });
    });
  };

  // Carousels: [data-pw-carousel] with a scrolling [data-pw-carousel-track], prev/next buttons and a
  // progress indicator. Native scrolling does the work; this only wires up the controls.
  const initCarousels = (scope = document) => {
    scope.querySelectorAll('[data-pw-carousel]').forEach((carousel) => {
      if (carousel.dataset.pwReady) return;
      carousel.dataset.pwReady = 'true';
      const track = carousel.querySelector('[data-pw-carousel-track]');
      const prev = carousel.querySelector('[data-pw-carousel-prev]');
      const next = carousel.querySelector('[data-pw-carousel-next]');
      const bar = carousel.querySelector('[data-pw-carousel-progress]');
      if (!track) return;
      const step = () => {
        const item = [...track.querySelectorAll('li')].find((li) => !li.hidden);
        const gap = parseFloat(getComputedStyle(track.firstElementChild).columnGap) || 16;
        return item ? item.getBoundingClientRect().width + gap : track.clientWidth * 0.8;
      };
      const update = () => {
        const max = track.scrollWidth - track.clientWidth;
        const ratio = track.scrollWidth ? track.clientWidth / track.scrollWidth : 1;
        if (bar) {
          bar.style.width = `${Math.min(100, Math.max(12, ratio * 100))}%`;
          const pos = max > 0 ? track.scrollLeft / max : 0;
          bar.style.transform = `translateX(${pos * (100 / Math.min(1, Math.max(0.12, ratio)) - 100)}%)`;
        }
        if (prev) prev.disabled = track.scrollLeft <= 2;
        if (next) next.disabled = track.scrollLeft >= max - 2;
        carousel.toggleAttribute('data-pw-static', max <= 2);
      };
      const behavior = reduceMotion ? 'auto' : 'smooth';
      prev?.addEventListener('click', () => track.scrollBy({ left: -step(), behavior }));
      next?.addEventListener('click', () => track.scrollBy({ left: step(), behavior }));
      track.addEventListener('scroll', () => requestAnimationFrame(update), { passive: true });
      window.addEventListener('resize', update);
      carousel.addEventListener('pw:filtered', () => requestAnimationFrame(update));
      update();
    });
  };

  root.classList.add('pw-js');
  const init = (scope) => {
    initReveal(scope);
    initStickyBars(scope);
    initFilters(scope);
    initCarousels(scope);
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }
  // Theme editor: re-run when sections are added or reloaded.
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
